import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cx } from './cx';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /** Disables the button and swaps the leading icon for a spinner. */
  loading?: boolean | undefined;
  fullWidth?: boolean | undefined;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
}

/**
 * `type` defaults to 'button'.
 *
 * HTML's default is 'submit', and a stray submit inside the driver's status
 * form would fire a mutation the driver did not ask for. Anything meant to
 * submit says so.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  iconStart,
  iconEnd,
  type = 'button',
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      // Both, on purpose: `disabled` stops the second tap on a slow connection,
      // and `aria-busy` is what tells a screen reader why nothing happened.
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cx(
        'dc-btn',
        variant !== 'primary' && `dc-btn--${variant}`,
        size !== 'md' && `dc-btn--${size}`,
        fullWidth && 'dc-btn--block',
        loading && 'dc-btn--loading',
        className,
      )}
    >
      {loading ? (
        <Spinner size="sm" />
      ) : (
        iconStart !== undefined && <span className="dc-btn__icon">{iconStart}</span>
      )}
      <span className="dc-btn__label">{children}</span>
      {iconEnd !== undefined && !loading && <span className="dc-btn__icon">{iconEnd}</span>}
    </button>
  );
}
